import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Drawer,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import type { IncidentDetail, IncidentOptions, IncidentTarafToken } from '@isg/shared-types';
import {
  useAksiyonAc,
  useCiktiIndir,
  useOlay,
  useOlayEylem,
  useOlayUygunsuzlugaTasi,
  useOlayOptions,
  useOlayEtkilenenGuncelle,
  useOlaySatirSil,
  useOlayRiskMaddeleri,
  useOlayTurDuzelt,
} from '../api/queries';
import { RiskEslesmePaneli } from './risk-parts';
import {
  MIKTAR_BIRIM_ETIKET,
  OLAY_AKIS_ETIKET,
  OLAY_TARAF_ETIKET,
  SAPKA_ETIKET,
  SIDDET_SIRA_ETIKET,
  olayDurumEtiketi,
  olayIslemEtiketi,
} from '../i18n/tr';
import { kayipGunEksikMi, kayipGunGirilebilirMi, tarafKimlikSinifi } from '@isg/validation';
import { AksiyonFormAlanlari } from './AksiyonlarScreen';
import { EkipBolumu, KronolojiBolumu, OrtamBolumu } from './olay-arastirma-parts';
import { DofSayaci } from './olay-dof-sayaci';
import { GundemineEkleDugmesi } from './kurul-parts';
import { OlayEkBolumu } from './olay-ek-parts';
import { SebepAgaci, SebepYuzeyi, YontemSecici } from './olay-sebep-parts';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { CokenBolum, KapiSebebi, Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { gunGirdisiIso, tarihGoster, zamanGoster } from '../lib/format';

/**
 * Olay detayı (F3B·S2 — Kapsam §16.8/14). DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN
 * (`yetkiler` — S4 deseni): ekran ikinci kural yazmaz; görünmeyen düğmenin
 * ucu da aynı kapıdan reddeder.
 *
 * - ANONİM kayıtta bildiren alanı HİÇ render edilmez (U3 — kimlik kayıtta
 *   zaten yok; ağdan inen yanıt da taşımaz).
 * - Kök sebep: GRUP ETİKETLİ TEK LİSTE (T1); yalnız 1. katman işaretiyle
 *   kapanış UYARI döner, ENGELLEMEZ (O11 — uyarı kapanış yanıtında).
 * - 5x5 yalnız değerlendirme akışında + akış başlatılmış (R3); potansiyel
 *   yalnız araştırma akışında — ikisi de servis kurallarının EKRAN yüzüdür.
 * - DÖF aç: MEVCUT aksiyon açma formu `incidentId` ile (U11 — aynı kapı,
 *   yeni uç yok).
 * - ✏️ T2·0 (Kapsam §16.8/7-j): KAPANIŞ TÜRETİLİR — "Olayı kapat" düğmesi
 *   YOK; DÖF sayacı (`DofSayaci`) durum göstergesidir, "Kapandı" rozeti
 *   `kapali` alanından. Adım şeridi + orta buton satırı KALKTI; akış
 *   başlatma / red / tür düzeltme / yükseltme BAŞLIK satırında (P2:
 *   bir birincil + ⋮). Kapalı olayda içerik salt okunur (K6 —
 *   `arastirmaKaydiYazabilir`), sebep satırından DÖF açmak AÇIK (yeni DÖF
 *   kaydı yeniden açar). K1/K10 kalıcı bantlar SUNUCUDAN türer.
 */
export function OlayDetayScreen(): React.JSX.Element {
  const { incidentId } = useParams();
  const olay = useOlay(incidentId ?? null);
  const options = useOlayOptions();
  const navigate = useNavigate();
  const indir = useCiktiIndir();

  if (!incidentId) return <Text>Kayıt bulunamadı.</Text>;
  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="sm" align="center">
          <Title order={3} data-testid="olay-baslik">
            {olay.data ? `Olay ${olay.data.takipNo}` : 'Olay'}
          </Title>
          {/* Türetilmiş kapanış rozeti (7-j.3) — kolon değil, DÖF'lerden. */}
          {olay.data?.kapali && (
            <Rozet anlam="olumlu" testId="olay-kapali-rozet">
              Kapandı
            </Rozet>
          )}
          {/* Madde 310 — DEĞİŞİKLİK GEÇMİŞİ: en az bir izli değişiklik varsa link
              (yoksa görünmez); yan panel satır satır (alan · eski → yeni · kişi ·
              zaman). İz sunucudan, değişmez; ekran yalnız etiket çözer. */}
          {olay.data && olay.data.degisiklikGecmisi.length > 0 && (
            <DegisiklikGecmisi olay={olay.data} />
          )}
        </Group>
        <Group gap="xs">
          {/* 7-j.6: geçiş eylemleri BAŞLIK satırında — birincil + ⋮ (P2-c). */}
          {olay.isSuccess && options.isSuccess && (
            <BaslikEylemleri olay={olay.data} options={options.data} />
          )}
          {/* İSG kurulu (§28/C6): görünürlük + engel sunucudan — kurul yoksa görünmez. */}
          {olay.isSuccess && (
            <GundemineEkleDugmesi
              workplaceId={olay.data.workplaceId}
              baslik={`Olay ${olay.data.takipNo}`}
              kaynak={{ incidentId: olay.data.id }}
              testId="olay-gundeme-ekle"
            />
          )}
          {/* Araştırma raporu (F4·B2·2 — S9 motoru): çıktı bir OKUMA
              hakkıdır — kaydı görebilen raporu da alır (izleyici dahil). */}
          {olay.isSuccess && (
            <Button
              variant="light"
              data-testid="olay-rapor-indir"
              loading={indir.isPending}
              onClick={() =>
                indir.mutate({
                  yol: `/api/incidents/${incidentId}/rapor`,
                  yedekAd: `olay-arastirma-raporu-${olay.data.takipNo}.pdf`,
                })
              }
            >
              Araştırma raporu (PDF)
            </Button>
          )}
          <Button variant="default" onClick={() => void navigate('/olaylar')}>
            Listeye dön
          </Button>
        </Group>
      </Group>
      <QueryStatus queries={[olay, options]} kayit />
      {olay.isSuccess && options.isSuccess && (
        <OlayGovdesi olay={olay.data} options={options.data} />
      )}
    </Stack>
  );
}

function OlayGovdesi({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  // F4·B4 — sebep satırından DÖF: seçilen düğüm modala taşınır.
  const [dofSebep, setDofSebep] = useState<IncidentDetail['sebepler'][number] | null>(null);
  // HEDEF SORGU 1 (F3B·S9 — §16.12/12): *"bu konu risk değerlendirmesinde
  // işlenmiş mi?"* Olayın LOKASYONU ∪ MEKANİZMASI üzerinden eşleşen risk
  // maddeleri. **OKUMA YÜZEYİ, KAPI DEĞİL** — hiçbir akışı engellemez.
  const riskMaddeleri = useOlayRiskMaddeleri(olay.id);

  // ✏️ B1c (yüzey dürüstlüğü turu · ikinci oturum) — ARAŞTIRMA BÖLÜMLERİNİN TEK
  // KAYNAĞI SUNUCUDUR: `arastirmaKapisi` = `olay_inceleme` kapısı ∧ içerik kilidi
  // (`assertIncidentMutable`in yüklemi). Eskiden beş bölümün üçü kapanış hâlini
  // burada "reddedilmemiş ∧ kapalı değil" diye YENİDEN türetiyordu — aynı
  // soruya iki cevap. Ekran artık hiçbir durum koşulu yazmaz.
  const arastirma = olay.yetkiler.arastirmaKapisi.yazabilir;

  return (
    <Stack>
      {/* K1 — KÖK SEBEP İŞARETLENMEMİŞ: türetilmiş KALICI band (kapalı olayda
          da görünür — R13 deseni). KAPI DEĞİL: DÖF açma ve kapanış türetmesi
          bundan bağımsızdır; band yalnız görünür kılar, rapor şerh basar. */}
      {olay.kokSebepIsaretsiz && (
        <Alert color={ANLAM.dikkat} data-testid="olay-kok-sebep-isaretsiz">
          Kök sebep işaretlenmemiş — sebep ağacında en az bir düğümü kök sebep olarak işaretleyin
          (konsolidasyon işaretli kalemleri sayar; kapanışı engellemez).
        </Alert>
      )}
      {/* K10 — O11 katman uyarısı, artık kapanış anı değil KALICI band. */}
      {olay.kokSebepYalnizDogrudan && (
        <Alert color={ANLAM.dikkat} data-testid="olay-kok-sebep-yalniz-dogrudan">
          Kök sebep işaretleri yalnız 1. katmanda (doğrudan sebepler) — neden zinciri sürüyor
          olabilir.
        </Alert>
      )}
      {/* D8: eşiksiz sınıf uyarısı — türetilmiş alan (engellemez), sunucudan. */}
      {olay.seviyeUyarilari.map((uyari) => (
        <Alert key={uyari} color={ANLAM.dikkat} data-testid="olay-seviye-uyari">
          {uyari}
        </Alert>
      ))}
      {/* R13 (F3B·S5): "dosya eksik" TÜRETİLMİŞ işaret — sunucudan gelir
          (kaza + kapanmamış + canlı SGK eki yok); kapanışı ENGELLEMEZ. */}
      {olay.dosyaEksik && (
        <Alert color={ANLAM.dikkat} data-testid="olay-dosya-eksik">
          SGK bildirim belgesi ekli değil — kaza dosyası eksik (kapanışı engellemez).
        </Alert>
      )}
      {/* Madde 309-b (08.10.2026): kayıp günlü basamakta (3–4) kayıp gün boş —
          R13 deseni: TÜRETİLMİŞ, kalıcı (kapalı kayıtta da), kapanışı
          ENGELLEMEZ. Ölümlü vakanın boş değeri meşrudur ve sayılmaz. */}
      {olay.kayipGunEksikVaka > 0 && (
        <Alert color={ANLAM.dikkat} data-testid="olay-kayip-gun-eksik">
          {olay.kayipGunEksikVaka} etkilenen kişide kayıp gün girilmemiş — kayıp günlü basamakta
          beklenir (kapanışı engellemez).
        </Alert>
      )}
      {/* ✏️ T2·0 (7-j.6): adım şeridi (B1) ve orta gövdedeki geçiş buton
          satırı KALKTI — durum künyede + başlık rozetinde; eylemler başlıkta. */}
      {/* C1+C2 — KÜNYE (kısa alanlar IZGARADA — sözleşme) + olayın tanımı.
          GEZİNTİ ARTÇISI (30.08.2026): sınıflandırma ve potansiyel şiddet
          ayrı kart olmaktan çıktı, künyenin devamı oldu — TEK BÖLGE, TEK
          KAYDET (B4 çözümünün tamamlanışı). EKRAN SIRASI RAPOR SIRASINI
          İZLER (B6) — bu bilinçli sapma aynen: raporun anlatı sırası ("önce
          ne oldu, sonra neden oldu") değişmedi. Kalan sıra: ortam ·
          kronoloji · etkilenenler · tanıklar · kanıtlar · yöntem+ekip ·
          sebep ağacı · hasarlar. */}
      <KunyeKarti olay={olay} options={options} />

      {/* C3-C4 — ortam her akışta (tehlikeli durum da bir ortamda gözlenir);
          kronoloji yalnız araştırma akışında (R2 — serviste de aynı kural).
          Yazma denetimleri SUNUCUDAN (`arastirmaKaydiYazabilir` — §2.40).
          Boş liste bölümleri TEK SATIRA çöker (P11 — CokenBolum). */}
      <OrtamBolumu olay={olay} options={options} />
      {/* P13 — araştırma bölümlerini yazamayana TEK sebep satırı (kapı + içerik kilidi). */}
      <KapiSebebi
        durum={olay.yetkiler.arastirmaKapisi}
        nesne="Araştırma kayıtlarını"
        fiil="yazar"
        testId="olay-arastirma-yazamama-sebebi"
      />
      {olay.akis === 'arastirma' && (
        <CokenBolum
          baslik="Kronoloji"
          sayi={olay.kronoloji.length}
          testId="olay-kronoloji"
          ekleyebilir={arastirma}
        >
          <KronolojiBolumu olay={olay} />
        </CokenBolum>
      )}

      {/* C5-C6 — etkilenen kişiler (+ yaralanma satırları) ve tanıklar. */}
      {olay.akis === 'arastirma' && (
        <CokenBolum
          baslik="Etkilenen kişiler"
          sayi={olay.etkilenenler.length}
          testId="olay-etkilenenler"
          ekleyebilir={arastirma}
        >
          <EtkilenenlerKarti
            olay={olay}
            options={options}
            inceleme={arastirma}
            kayipGunDuzenlenir={olay.yetkiler.kayipGunKapisi.yazabilir}
          />
        </CokenBolum>
      )}
      <CokenBolum
        baslik="Tanıklar"
        sayi={olay.taniklar.length}
        testId="olay-taniklar"
        ekleyebilir={arastirma}
      >
        <TaniklarKarti olay={olay} inceleme={arastirma} />
      </CokenBolum>

      {/* C7 — EKLER/KANIT (F4·B2 · B5 + R13). Durum kapısı YOK (SGK belgesi
          kapanıştan sonra da gelir); kanıt kategorisi tipten ayrı eksen. */}
      <OlayEkBolumu olay={olay} />

      {/* ✏️ **TUR 11 / R3-a: 5x5 YAZMA KARTI DÜŞTÜ.** Olayın değerlendirme
          akışı emekliye ayrıldı (§16.8/9 revizyonu) ve araç uygunsuzluğa
          taşındı (§24.1/13). ⚠️ **TARİHÇE HER İKİ AKIŞTA DA KALIR** —
          mevcut kayıtların puanı okunmaya devam eder; kapanan şey GİRİŞ'tir,
          kayıt değil. Potansiyel şiddet KÜNYEDE (majör rozetin yanında). */}
      {olay.puan5x5 !== null && <Bes5Tarihce olay={olay} />}

      {/* C9 — araştırma yöntemi ve ekibi. */}
      {olay.akis === 'arastirma' && arastirma && <YontemBolumu olay={olay} />}
      {olay.akis === 'arastirma' && (
        <CokenBolum
          baslik="Araştırma ekibi"
          sayi={olay.arastirmacilar.length}
          testId="olay-ekip"
          ekleyebilir={arastirma}
        >
          <EkipBolumu olay={olay} options={options} />
        </CokenBolum>
      )}

      {/* C10 — sebep ağacı: doldurma yüzeyi (yazabilirse) + değişmez tek
          ağaç görünümü. DÖF SEBEP SATIRINDAN açılır (F4·B4 — "olayın
          DÖF'ünü açmıyoruz, sebeplere açıyoruz"); genel düğme araştırmada
          KALKTI, sebepsiz genel DÖF talep akışından hâlâ açılabilir (O6). */}
      {olay.akis === 'arastirma' && arastirma && (
        <SebepDoldurmaBolumu olay={olay} options={options} />
      )}
      {/* K6: sebep satırından DÖF açmak KAPALI olayda da AÇIK (yeni DÖF kaydı
          yeniden açar) — düğme pasifleştirilmez, şart konmaz (K1). */}
      {olay.sebepler.length > 0 && (
        <Card withBorder data-testid="olay-sebepler">
          <Title order={5}>Sebep ağacı</Title>
          <SebepAgaci
            olay={olay}
            yazabilir={olay.yetkiler.sebepYazabilir}
            onDofAc={
              olay.yetkiler.dofAcabilir && olay.durum === 'investigating'
                ? (satir) => setDofSebep(satir)
                : undefined
            }
          />
        </Card>
      )}
      <Modal
        opened={dofSebep !== null}
        onClose={() => setDofSebep(null)}
        title="Sebepten DÖF aç"
        size="lg"
      >
        {dofSebep && <DofFormu olay={olay} sebep={dofSebep} onClose={() => setDofSebep(null)} />}
      </Modal>

      {/* C12 — hasar kalemleri (C11 DÖF sayacı künyede yaşar). */}
      {olay.akis === 'arastirma' && (
        <CokenBolum
          baslik="Hasar kalemleri"
          sayi={olay.hasarlar.length}
          testId="olay-hasarlar"
          ekleyebilir={arastirma}
        >
          <HasarlarKarti olay={olay} inceleme={arastirma} />
        </CokenBolum>
      )}

      {/* F3B·S9 — okuma yüzeyi (rapor sırasının dışında): bulunursa "bu risk
          değerlendirilmişti, önlem şuydu". Cümle SUNUCUDAN gelir. */}
      <Card withBorder>
        <RiskEslesmePaneli
          baslik="Risk değerlendirmesinde işlenmiş mi?"
          veri={riskMaddeleri.data}
          testId="olay-risk-paneli"
        />
      </Card>

      <GecisGecmisi olay={olay} />
    </Stack>
  );
}

/**
 * C1 — KÜNYE IZGARADA (P9) + TEK KAYDET BÖLGESİ (gezinti artçısı,
 * 30.08.2026). Sınıflandırma (sınıf onay kutuları + mekanizma + sınıfa özel
 * alanlar) ve potansiyel şiddet ayrı kart DEĞİL, künyenin devamıdır;
 * lokasyon + ekipman bağlarıyla (A1) birlikte hepsi künyenin TEK "Kaydet"
 * düğmesine bağlıdır. SUNUCU SÖZLEŞMESİ DEĞİŞMEDİ — uçlar (`kunye` ·
 * `siniflar` · `alanlar` · `potansiyel-siddet`) sırayla, yalnız KİRLİ olan
 * gruplar için çağrılır (B4'ün "iki uç sırayla" deseninin genişlemesi;
 * birleşik uç yazılmadı — uç sözleşmesi ekran ergonomisine feda edilmez).
 *
 * Görünürlük `arastirmaKaydiYazabilir` (sunucu kapısının ikizi — §2.40:
 * yazamayan salt okunur değer görür; temiz durumda Kaydet KAPALIDIR, basınca
 * hiçbir şey yapmayan düğme bırakılmaz). Sınıf ekseni DÖRT türde de
 * işaretlenir (D2 — akışa bağlı değil); potansiyel şiddet yalnız ARAŞTIRMA
 * akışında (R3 — değerlendirmede o rolü 5x5 devralır) ve majör rozetin
 * YANINDA durur (rozet ondan türer — yan yana anlamlı).
 */
function KunyeKarti({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const yazabilir = olay.yetkiler.arastirmaKaydiYazabilir;
  const [lokasyon, setLokasyon] = useState<string | null>(olay.locationNodeId);
  const [ekipman, setEkipman] = useState<string | null>(olay.equipmentId);
  const lokasyonSecenekleri = options.lokasyonlar
    .filter((l) => l.workplaceId === olay.workplaceId)
    .map((l) => ({ value: l.id, label: l.ad }));
  const ekipmanSecenekleri = options.ekipmanlar
    .filter((e) => e.workplaceId === olay.workplaceId)
    .map((e) => ({ value: e.id, label: e.ad }));

  // Sınıflandırma (F3B·S3 — künyeye taşındı): "Diğer" serbest metin
  // zorunlulukları SERVİSTE (§19/13) — ekran alanı gösterir, kural yazmaz.
  // Çevre/proses alanları yalnız o girdili sınıf SEÇİLİYKEN çizilir (işaret
  // sunucudan: options.olaySiniflari[].turetmeGirdisi).
  const [sinifIds, setSinifIds] = useState<string[]>(olay.siniflar.map((s) => s.sinifRefId));
  const [sinifDigerMetin, setSinifDigerMetin] = useState(
    olay.siniflar.find((s) => s.serbestMetin !== null)?.serbestMetin ?? '',
  );
  const [mekanizmaId, setMekanizmaId] = useState<string | null>(olay.mekanizmaRefId);
  const [mekanizmaMetin, setMekanizmaMetin] = useState(olay.mekanizmaDigerMetin ?? '');
  const alanlar = olay.sinifAlanlari;
  const [yayilimId, setYayilimId] = useState<string | null>(alanlar.yayilimSiniriRefId);
  const [salinanMiktar, setSalinanMiktar] = useState<number | string>(alanlar.salinanMiktar ?? '');
  const [salinanBirim, setSalinanBirim] = useState<string | null>(alanlar.salinanBirim);
  const [resmiBildirim, setResmiBildirim] = useState(alanlar.resmiBildirimeTabi ?? false);
  const [kayipMiktari, setKayipMiktari] = useState<number | string>(alanlar.kayipMiktari ?? '');
  const [kayipBirimi, setKayipBirimi] = useState<string | null>(alanlar.kayipBirimi);
  const [maddeId, setMaddeId] = useState<string | null>(alanlar.maddeTehlikeSinifiRefId);
  const [maddeMetin, setMaddeMetin] = useState(alanlar.maddeSinifiDigerMetin ?? '');
  const [ikincilSonuc, setIkincilSonuc] = useState(alanlar.ikincilSonuc ?? false);

  // O8 — potansiyel şiddet ELLE seçilir (asimetri bilinçli); yalnız
  // araştırma akışında sorulur (R3).
  const [potansiyel, setPotansiyel] = useState<string | null>(
    olay.potansiyelSiddetSira === null ? null : String(olay.potansiyelSiddetSira),
  );
  const potansiyelYazilir = yazabilir && olay.akis === 'arastirma';

  const sinifById = useMemo(
    () => new Map(options.olaySiniflari.map((s) => [s.id, s])),
    [options.olaySiniflari],
  );
  const digerSecili = sinifIds.some((id) => sinifById.get(id)?.digerMi);
  const cevreSecili = sinifIds.some((id) => sinifById.get(id)?.turetmeGirdisi === 'yayilim');
  const prosesSecili = sinifIds.some((id) => sinifById.get(id)?.turetmeGirdisi === 'esik_tablosu');
  const mekanizmaDigerMi = mekanizmaId
    ? (options.mekanizmalar.find((m) => m.id === mekanizmaId)?.digerMi ?? false)
    : false;
  const maddeDigerMi = maddeId
    ? (options.maddeSiniflari.find((m) => m.id === maddeId)?.digerMi ?? false)
    : false;
  const birimData = Object.entries(MIKTAR_BIRIM_ETIKET).map(([value, label]) => ({
    value,
    label,
  }));

  // KİRLİLİK GRUP GRUP izlenir: Kaydet yalnız kirli grupların ucunu çağırır.
  const baglarKirli = lokasyon !== olay.locationNodeId || ekipman !== olay.equipmentId;
  const siniflarKirli =
    sinifIds.length !== olay.siniflar.length ||
    sinifIds.some((id) => !olay.siniflar.some((s) => s.sinifRefId === id)) ||
    sinifDigerMetin !== (olay.siniflar.find((s) => s.serbestMetin !== null)?.serbestMetin ?? '');
  const alanlarKirli =
    mekanizmaId !== olay.mekanizmaRefId ||
    mekanizmaMetin !== (olay.mekanizmaDigerMetin ?? '') ||
    yayilimId !== alanlar.yayilimSiniriRefId ||
    String(salinanMiktar) !== String(alanlar.salinanMiktar ?? '') ||
    salinanBirim !== alanlar.salinanBirim ||
    resmiBildirim !== (alanlar.resmiBildirimeTabi ?? false) ||
    String(kayipMiktari) !== String(alanlar.kayipMiktari ?? '') ||
    kayipBirimi !== alanlar.kayipBirimi ||
    maddeId !== alanlar.maddeTehlikeSinifiRefId ||
    maddeMetin !== (alanlar.maddeSinifiDigerMetin ?? '') ||
    ikincilSonuc !== (alanlar.ikincilSonuc ?? false);
  const potansiyelKirli =
    potansiyelYazilir && potansiyel !== null && Number(potansiyel) !== olay.potansiyelSiddetSira;
  const kirli = baglarKirli || siniflarKirli || alanlarKirli || potansiyelKirli;
  // Madde 221 — kaydedilmemiş künye değişikliğiyle sayfadan çıkış YÖNLENDİRİCİDE uyarılır.
  useKaydedilmemisIcerik(kirli ? 1 : 0, 'künye değişikliği');

  return (
    <Card withBorder data-testid="olay-kunye">
      <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
        <Kunye
          etiket="Tarih · saat"
          deger={`${tarihGoster(olay.olayTarihi)}${olay.ortam.olaySaati ? ` · ${olay.ortam.olaySaati}` : ''}`}
        />
        <Kunye etiket="İşyeri" deger={olay.workplaceAd} />
        <Kunye etiket="Lokasyon" deger={olay.locationAd ?? '—'} />
        <Kunye etiket="İş ekipmanı" deger={olay.equipmentAd ?? '—'} />
        <Kunye etiket="Tür" deger={olay.olayTuruAd ?? 'Kaza (uzman kaydı)'} />
        <Kunye etiket="Akış" deger={OLAY_AKIS_ETIKET[olay.akis] ?? olay.akis} />
        <Kunye
          etiket="Durum"
          deger={olayDurumEtiketi(olay.durum, olay.akis, olay.kapali)}
          testId="olay-kunye-durum"
        />
        {olay.kapali && olay.kapanisAt && (
          <Kunye etiket="Kapanış (son DÖF)" deger={tarihGoster(olay.kapanisAt)} />
        )}
        {/* U3/R5: anonim kayıtta bildiren SATIRI hiç çizilmez. */}
        {!olay.anonim && <Kunye etiket="Bildiren" deger={olay.bildirenAd ?? '—'} />}
        {olay.gerceklesenSiddetSira !== null && (
          <Kunye
            etiket="Gerçekleşen şiddet (türetilir)"
            deger={
              SIDDET_SIRA_ETIKET[olay.gerceklesenSiddetSira] ?? String(olay.gerceklesenSiddetSira)
            }
          />
        )}
        {/* 7-j.5 — DÖF sayacı DURUM GÖSTERGESİDİR: renk tek yüklemden. */}
        <Stack gap={0}>
          <Text size="xs" c="dimmed">
            DÖF
          </Text>
          <div>
            <DofSayaci
              toplam={olay.toplamDofSayisi}
              acik={olay.acikDofSayisi}
              testId="olay-dof-sayaci"
            />
          </div>
        </Stack>
      </SimpleGrid>
      <Group gap="md" mt="xs" align="center" wrap="wrap">
        {/* Potansiyel şiddet KÜNYEDE, majör rozetin YANINDA (rozet ondan
            türer). Yazamayan değeri salt okunur görür (§2.40); kap testid'i
            e2e'nin "araştırma alanı görünür" işaretidir. */}
        {olay.akis === 'arastirma' && (potansiyelYazilir || olay.potansiyelSiddetSira !== null) && (
          <Box data-testid="olay-arastirma">
            {potansiyelYazilir ? (
              <Select
                label="Potansiyel şiddet (ELLE — O8; majör işaret bundan türetilir)"
                size="xs"
                w={300}
                data-testid="olay-potansiyel"
                data={Object.entries(SIDDET_SIRA_ETIKET).map(([sira, ad]) => ({
                  value: sira,
                  label: `${sira} — ${ad}`,
                }))}
                value={potansiyel}
                onChange={setPotansiyel}
              />
            ) : (
              <Kunye
                etiket="Potansiyel şiddet"
                deger={
                  olay.potansiyelSiddetSira === null
                    ? '—'
                    : (SIDDET_SIRA_ETIKET[olay.potansiyelSiddetSira] ??
                      String(olay.potansiyelSiddetSira))
                }
              />
            )}
          </Box>
        )}
        {olay.anonim && <Badge color={ANLAM.bilgi}>Anonim bildirim</Badge>}
        {olay.majorArastirma && (
          <Badge color={ANLAM.engel} data-testid="olay-major-rozet">
            Majör araştırma
          </Badge>
        )}
        {/* F4·B4 — yükseltme ÖNERİSİ (majör rozet ailesi): sistem önerir,
            karar uzmanda — otomatik geçiş yok. İşaret SUNUCUDAN türer. */}
        {olay.yukseltmeOnerisi && (
          <Badge color={ANLAM.dikkat} data-testid="olay-yukselt-oneri">
            Araştırmaya yükseltme önerilir
          </Badge>
        )}
        {/* TÜRETİLEN seviye rozeti (O5-O7) — tehlikeli durum/davranışta
            seviye null'dır ve rozet HİÇ ÇİZİLMEZ (R3). */}
        {olay.seviye && (
          <Badge color={ANLAM.bilgi} data-testid="olay-seviye-rozet">
            {`Seviye ${olay.seviye.sira} — ${olay.seviye.ad}`}
          </Badge>
        )}
      </Group>
      {/* A1 — lokasyon + ekipman bağlama (uzun kontroller ızgaranın altında).
          Ekipman bağının değeri İSTATİSTİK: "bu ekipman kaç olayda". */}
      {yazabilir && (
        <Group align="end" gap="sm" mt="sm" wrap="wrap">
          {lokasyonSecenekleri.length > 0 && (
            <Select
              label="Lokasyon"
              size="xs"
              clearable
              searchable
              w={220}
              data-testid="olay-kunye-lokasyon"
              data={lokasyonSecenekleri}
              value={lokasyon}
              onChange={setLokasyon}
            />
          )}
          {ekipmanSecenekleri.length > 0 && (
            <Select
              label="İş ekipmanı"
              size="xs"
              clearable
              searchable
              w={220}
              data-testid="olay-kunye-ekipman"
              data={ekipmanSecenekleri}
              value={ekipman}
              onChange={setEkipman}
            />
          )}
        </Group>
      )}
      {/* SINIFLANDIRMA — künye ızgarasının devamı (ayrı kart/başlık YOK).
          Sınıf ÇOKLU (O1); onay kutusu IZGARASI (P10 — az kalemli çoklu
          seçim açılır listeye gömülmez). */}
      {yazabilir && (
        <Box mt="sm" data-testid="olay-siniflandirma">
          <Checkbox.Group
            label="Olay sınıfı (ÇOKLU — sınıf toplamı ≠ olay sayısı)"
            value={sinifIds}
            onChange={setSinifIds}
          >
            <SimpleGrid
              cols={{ base: 2, sm: 3 }}
              spacing="xs"
              mt={6}
              data-testid="olay-sinif-secim"
            >
              {options.olaySiniflari.map((s, sira) => (
                <Checkbox
                  key={s.id}
                  value={s.id}
                  label={s.ad}
                  data-testid={`olay-sinif-kutu-${sira}`}
                />
              ))}
            </SimpleGrid>
          </Checkbox.Group>
          {digerSecili && (
            <TextInput
              label='Serbest metin ("Diğer" sınıfında zorunlu)'
              data-testid="olay-sinif-diger-metin"
              mt="sm"
              value={sinifDigerMetin}
              onChange={(event) => setSinifDigerMetin(event.currentTarget.value)}
            />
          )}
          <Group align="end" mt="sm" wrap="wrap">
            <Select
              label="Mekanizma"
              searchable
              clearable
              data-testid="olay-mekanizma"
              data={options.mekanizmalar.map((m) => ({ value: m.id, label: m.ad }))}
              value={mekanizmaId}
              onChange={setMekanizmaId}
              w={260}
            />
            {mekanizmaDigerMi && (
              <TextInput
                label='Serbest metin ("Diğer" mekanizmada zorunlu)'
                data-testid="olay-mekanizma-diger-metin"
                value={mekanizmaMetin}
                onChange={(event) => setMekanizmaMetin(event.currentTarget.value)}
              />
            )}
            {cevreSecili && (
              <>
                <Select
                  label="Yayılım sınırı (türetmenin ağırlığı — D6)"
                  data-testid="olay-yayilim"
                  clearable
                  data={options.yayilimSiniflari.map((y) => ({
                    value: y.id,
                    label: y.sira === null ? y.ad : `${y.sira} — ${y.ad}`,
                  }))}
                  value={yayilimId}
                  onChange={setYayilimId}
                  w={280}
                />
                <NumberInput
                  label="Salınan miktar"
                  min={0}
                  value={salinanMiktar}
                  onChange={setSalinanMiktar}
                  w={140}
                />
                <Select
                  label="Birim"
                  clearable
                  data={birimData}
                  value={salinanBirim}
                  onChange={setSalinanBirim}
                  w={90}
                />
                <Checkbox
                  label="Resmî bildirime tabi"
                  checked={resmiBildirim}
                  onChange={(event) => setResmiBildirim(event.currentTarget.checked)}
                />
              </>
            )}
            {prosesSecili && (
              <>
                <NumberInput
                  label="Kayıp miktarı (birincil muhafaza)"
                  min={0}
                  value={kayipMiktari}
                  onChange={setKayipMiktari}
                  w={180}
                />
                <Select
                  label="Birim"
                  clearable
                  data={birimData}
                  value={kayipBirimi}
                  onChange={setKayipBirimi}
                  w={90}
                />
                <Select
                  label="Madde tehlike sınıfı (SIRASIZ — K7)"
                  searchable
                  clearable
                  data-testid="olay-madde-sinifi"
                  data={options.maddeSiniflari.map((m) => ({ value: m.id, label: m.ad }))}
                  value={maddeId}
                  onChange={setMaddeId}
                  w={240}
                />
                {maddeDigerMi && (
                  <TextInput
                    label='Serbest metin ("Diğer" madde sınıfında zorunlu)'
                    value={maddeMetin}
                    onChange={(event) => setMaddeMetin(event.currentTarget.value)}
                  />
                )}
                <Checkbox
                  label="İkincil sonuç (yangın/patlama gerçekleşti)"
                  checked={ikincilSonuc}
                  onChange={(event) => setIkincilSonuc(event.currentTarget.checked)}
                />
              </>
            )}
          </Group>
        </Box>
      )}
      {/* KÜNYEDE TEK KAYDET: bağlar + sınıf + mekanizma/alanlar + potansiyel
          şiddet birlikte. Yalnız KİRLİ grupların ucu çağrılır; temizken düğme
          KAPALI (basınca hiçbir şey yapmayan düğme = §2.40 arızası). */}
      {yazabilir && (
        <Group mt="md">
          <Button
            data-testid="olay-kunye-kaydet"
            loading={eylem.isPending}
            disabled={!kirli}
            rightSection={
              kirli ? (
                <Rozet anlam="dikkat" testId="olay-kunye-kirli">
                  kaydedilmedi
                </Rozet>
              ) : undefined
            }
            onClick={() => {
              void (async () => {
                if (baglarKirli) {
                  await eylem.mutateAsync({
                    id: olay.id,
                    eylem: 'kunye',
                    govde: { locationNodeId: lokasyon, equipmentId: ekipman },
                  });
                }
                if (siniflarKirli) {
                  await eylem.mutateAsync({
                    id: olay.id,
                    eylem: 'siniflar',
                    govde: {
                      siniflar: sinifIds.map((sinifRefId) => ({
                        sinifRefId,
                        ...(sinifById.get(sinifRefId)?.digerMi && sinifDigerMetin.trim() !== ''
                          ? { serbestMetin: sinifDigerMetin.trim() }
                          : {}),
                      })),
                    },
                  });
                }
                if (alanlarKirli) {
                  await eylem.mutateAsync({
                    id: olay.id,
                    eylem: 'alanlar',
                    govde: {
                      mekanizmaRefId: mekanizmaId,
                      mekanizmaDigerMetin:
                        mekanizmaMetin.trim() === '' ? null : mekanizmaMetin.trim(),
                      yayilimSiniriRefId: yayilimId,
                      salinanMiktar: salinanMiktar === '' ? null : Number(salinanMiktar),
                      salinanBirim,
                      resmiBildirimeTabi: resmiBildirim,
                      kayipMiktari: kayipMiktari === '' ? null : Number(kayipMiktari),
                      kayipBirimi,
                      maddeTehlikeSinifiRefId: maddeId,
                      maddeSinifiDigerMetin: maddeMetin.trim() === '' ? null : maddeMetin.trim(),
                      ikincilSonuc,
                    },
                  });
                }
                if (potansiyelKirli && potansiyel !== null) {
                  await eylem.mutateAsync({
                    id: olay.id,
                    eylem: 'potansiyel-siddet',
                    govde: { sira: Number(potansiyel) },
                  });
                }
              })().catch(() => {
                // Hata eylem.isError üzerinden ekranda görünür — burada
                // yutulur ki unhandled rejection uyarısı doğmasın.
              });
            }}
          >
            Kaydet
          </Button>
        </Group>
      )}
      {eylem.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="olay-kunye-hata" mt="xs">
          {eylem.error.message}
        </Text>
      )}
      <Text mt="sm" size="sm" data-testid="olay-anlati">
        {olay.anlati}
      </Text>
      {olay.durum === 'rejected' && (
        <Alert mt="sm" color={ANLAM.engel} data-testid="olay-red-karti">
          Reddedildi — {olay.redGerekceAd ?? ''}: {olay.redGerekceMetin ?? ''}
          {olay.mukerrerHedefTakipNo && ` (mükerrer hedef: ${olay.mukerrerHedefTakipNo})`}
        </Alert>
      )}
    </Card>
  );
}

function Kunye({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      <Text size="sm" data-testid={testId}>
        {deger}
      </Text>
    </Stack>
  );
}

/**
 * BAŞLIK EYLEMLERİ (✏️ T2·0 — 7-j.6; P2-c): geçiş eylemleri kaydın başlık
 * satırında — BİR birincil (*Araştırma başlat* / *Değerlendirme başlat*),
 * kalanı ⋮ altında (Reddet · Türü düzelt · Araştırmaya yükselt · DÖF aç
 * [değerlendirme akışı — sebep ağacı yok]). Görünürlük `yetkiler`ten
 * (sunucudan); ekran ikinci kural yazmaz. "Olayı kapat" YOK — kapanış
 * türetilir (7-j.3).
 */
function BaslikEylemleri({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const [redAcik, setRedAcik] = useState(false);
  const [turAcik, setTurAcik] = useState(false);
  const [dofAcikMi, setDofAcikMi] = useState(false);
  const [yukseltAcik, setYukseltAcik] = useState(false);
  const [tasiAcik, setTasiAcik] = useState(false);
  const [tasiGerekce, setTasiGerekce] = useState('');
  const tasi = useOlayUygunsuzlugaTasi();
  const navigate = useNavigate();

  const y = olay.yetkiler;
  const baslatabilir = y.inceleyebilir && olay.durum === 'reported';
  const reddedebilir = y.inceleyebilir && olay.durum === 'reported';
  // F4·B4 — genel "DÖF aç" ARAŞTIRMADA KALKTI (sebep satırından açılır);
  // değerlendirme akışında KALIR — bilinçli sapma: o akışta sebep ağacı yok.
  const genelDofAcabilir =
    y.dofAcabilir && olay.akis === 'degerlendirme' && olay.durum === 'investigating';

  return (
    <>
      <SatirEylemleri
        testId="olay-baslik-eylemleri"
        birincil={
          baslatabilir
            ? {
                etiket: olay.akis === 'arastirma' ? 'Araştırma başlat' : 'Değerlendirme başlat',
                testId: 'olay-akis-baslat',
                onClick: () => eylem.mutate({ id: olay.id, eylem: 'start-flow' }),
                pasif: eylem.isPending,
              }
            : undefined
        }
        digerleri={[
          {
            etiket: 'Reddet',
            testId: 'olay-reddet',
            tehlikeli: true,
            gizli: !reddedebilir,
            onClick: () => setRedAcik(true),
          },
          {
            etiket: 'Türü düzelt',
            testId: 'olay-tur-duzelt',
            gizli: !y.turDuzeltebilir,
            onClick: () => setTurAcik(true),
          },
          {
            etiket: 'Araştırmaya yükselt',
            testId: 'olay-yukselt',
            gizli: !y.yukseltebilir,
            onClick: () => setYukseltAcik(true),
          },
          {
            etiket: 'DÖF aç',
            testId: 'olay-dof-ac',
            gizli: !genelDofAcabilir,
            onClick: () => setDofAcikMi(true),
          },
          // TUR 2½a (U7'nin diğer yönü): *"bir şey yaşanmamış, bir bozukluk
          // görülmüş"*. Yalnız YENİ BİLDİRİLMİŞ kayıtta — araştırması
          // başlamış olayda girilen içerik anlamsız kalırdı (kapı sunucuda
          // da aynı; ekran ÖNLER, yerine geçmez).
          {
            etiket: 'Uygunsuzluğa taşı',
            testId: 'olay-uygunsuzluga-tasi',
            gizli: !(y.inceleyebilir && olay.durum === 'reported'),
            onClick: () => setTasiAcik(true),
          },
        ]}
      />
      {eylem.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="olay-eylem-hata">
          {eylem.error.message}
        </Text>
      )}

      <Modal
        opened={tasiAcik}
        onClose={() => setTasiAcik(false)}
        title="Uygunsuzluğa taşı"
        size="md"
      >
        <Stack>
          <Text size="sm">
            Bu kayıt bir gerçekleşme değil, görülen bir bozukluksa Uygunsuzluklar'a taşınır. İçerik
            ve ekler yeni kayda geçer; bu kayıt &quot;Taşındı&quot; olarak kalır ve iki kayıt
            birbirini takip numarasıyla gösterir.
          </Text>
          {tasi.isError && (
            <Alert color={ANLAM.engel} role="alert" data-testid="olay-tasima-hata">
              {tasi.error.message}
            </Alert>
          )}
          <Textarea
            label="Gerekçe"
            data-testid="olay-tasima-gerekce"
            minRows={2}
            value={tasiGerekce}
            onChange={(e) => setTasiGerekce(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button
              data-testid="olay-tasima-kaydet"
              disabled={!tasiGerekce.trim() || tasi.isPending}
              onClick={() =>
                tasi.mutate(
                  { id: olay.id, gerekce: tasiGerekce.trim() },
                  {
                    onSuccess: (sonuc) => void navigate(`/uygunsuzluklar/${sonuc.uygunsuzluk!.id}`),
                  },
                )
              }
            >
              Taşı
            </Button>
          </Group>
        </Stack>
      </Modal>

      <RedModali olay={olay} options={options} opened={redAcik} onClose={() => setRedAcik(false)} />
      <TurDuzeltModali
        olay={olay}
        options={options}
        opened={turAcik}
        onClose={() => setTurAcik(false)}
      />
      <Modal
        opened={dofAcikMi}
        onClose={() => setDofAcikMi(false)}
        title="Olaydan DÖF aç"
        size="lg"
      >
        <DofFormu olay={olay} onClose={() => setDofAcikMi(false)} />
      </Modal>
      <Modal opened={yukseltAcik} onClose={() => setYukseltAcik(false)} title="Araştırmaya yükselt">
        <Stack>
          <Text size="sm">
            Değerlendirmedeki olay araştırma akışına geçer — yüksek potansiyelli olay gerçekleşmiş
            gibi araştırılır (yöntem · sebep ağacı · kronoloji · ekip bölümleri açılır).
          </Text>
          <Alert color={ANLAM.dikkat}>
            Bu geçiş TEK YÖNLÜDÜR: değerlendirmeye geri inilmez. 5x5 değerlendirme verisi silinmez —
            salt okunur tarihçe olarak kalır.
          </Alert>
          <Button
            data-testid="olay-yukselt-onayla"
            loading={eylem.isPending}
            onClick={() =>
              eylem.mutate(
                { id: olay.id, eylem: 'yukselt' },
                { onSuccess: () => setYukseltAcik(false) },
              )
            }
          >
            Araştırmaya yükselt
          </Button>
        </Stack>
      </Modal>
    </>
  );
}

function RedModali({
  olay,
  options,
  opened,
  onClose,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
  opened: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const [gerekceId, setGerekceId] = useState<string | null>(null);
  const [metin, setMetin] = useState('');
  const [hedefTakip, setHedefTakip] = useState('');
  const secilen = options.redGerekceleri.find((g) => g.id === gerekceId);
  const mukerrer = secilen?.hedefIster ?? false;
  return (
    <Modal opened={opened} onClose={onClose} title="Bildirimi reddet">
      <Stack>
        <Select
          label="Gerekçe"
          required
          data-testid="olay-red-gerekce"
          data={options.redGerekceleri.map((g) => ({ value: g.id, label: g.ad }))}
          value={gerekceId}
          onChange={setGerekceId}
        />
        <Textarea
          label="Serbest metin (zorunlu — R7)"
          required
          data-testid="olay-red-metin"
          value={metin}
          onChange={(event) => setMetin(event.currentTarget.value)}
        />
        {mukerrer && (
          <TextInput
            label="Mükerrer hedef kaydın ID'si (gerçek bağ — R7)"
            data-testid="olay-red-hedef"
            value={hedefTakip}
            onChange={(event) => setHedefTakip(event.currentTarget.value)}
          />
        )}
        {eylem.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert">
            {eylem.error.message}
          </Text>
        )}
        <Button
          data-testid="olay-red-onayla"
          disabled={!gerekceId || metin.trim() === ''}
          loading={eylem.isPending}
          onClick={() =>
            eylem.mutate(
              {
                id: olay.id,
                eylem: 'reject',
                govde: {
                  gerekceRefId: gerekceId,
                  gerekceMetin: metin.trim(),
                  ...(mukerrer && hedefTakip.trim() !== ''
                    ? { mukerrerHedefIncidentId: hedefTakip.trim() }
                    : {}),
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          Reddet
        </Button>
      </Stack>
    </Modal>
  );
}

function TurDuzeltModali({
  olay,
  options,
  opened,
  onClose,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
  opened: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const duzelt = useOlayTurDuzelt();
  const [turId, setTurId] = useState<string | null>(null);
  const [uyarilar, setUyarilar] = useState<string[]>([]);
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Olay türünü düzelt (U2 — yalnız kimlikli kayıt)"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Araştırma gerektiren olayın türü araştırma gerektirmeyen türe çevrilemez — gerekiyorsa
          olayı gerekçeyle reddedin ve doğru türle yeniden bildirin.
        </Text>
        {uyarilar.map((u) => (
          <Alert key={u} color={ANLAM.dikkat} data-testid="olay-tur-duzelt-uyari">
            {u}
          </Alert>
        ))}
        <Select
          label="Yeni tür"
          required
          data-testid="olay-tur-duzelt-secim"
          data={options.olayTurleri.map((t) => ({ value: t.id, label: t.ad }))}
          value={turId}
          onChange={setTurId}
        />
        {duzelt.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="olay-tur-duzelt-hata">
            {duzelt.error.message}
          </Text>
        )}
        <Button
          data-testid="olay-tur-duzelt-onayla"
          disabled={!turId}
          loading={duzelt.isPending}
          onClick={() =>
            turId &&
            duzelt.mutate(
              { id: olay.id, olayTuruRefId: turId },
              {
                onSuccess: (yanit) => {
                  // Uyarı ENGELLEMEZ (K8): varsa modal açık kalır ve okunur;
                  // yoksa kapanır.
                  if (yanit.uyarilar.length === 0) onClose();
                  setUyarilar(yanit.uyarilar);
                },
              },
            )
          }
        >
          Düzelt
        </Button>
      </Stack>
    </Modal>
  );
}

/**
 * Olaydan DÖF: MEVCUT aksiyon formu alanları + incidentId (U11 — aynı kapı).
 * F4·B4: `sebep` verilirse DÖF o sebep düğümüne bağlanır (incidentCauseId) —
 * araştırmada tek açılış yolu sebep satırıdır; değerlendirmede sebepsiz.
 */
function DofFormu({
  olay,
  sebep,
  onClose,
}: {
  olay: IncidentDetail;
  sebep?: IncidentDetail['sebepler'][number] | null;
  onClose: () => void;
}): React.JSX.Element {
  const ac = useAksiyonAc();
  // Aksiyon kancası kendi anahtarlarını tazeler; DÖF sayacı OLAY detayında
  // yaşadığı için incidents da tazelenir — sayaç bayat kalmasın (R9 kapısının
  // ekran yüzü).
  const queryClient = useQueryClient();
  const [atanan, setAtanan] = useState<string | null>(null);
  const [kaynak, setKaynak] = useState<string | null>(null);
  const [baslik, setBaslik] = useState('');
  const [termin, setTermin] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [referansNotu, setReferansNotu] = useState('');
  // ✏️ T6/T13: KAYNAK ARTIK SORULMUYOR — sunucu onu kayıttan türetiyor
  // (`kaynakTipiniCoz`). Seçim alanı açık kalsaydı kullanıcının seçtiği
  // değer SESSİZCE yok sayılırdı (§2.40 — yalan söyleyen kontrol).
  const hazir = Boolean(atanan) && baslik.trim() !== '' && gunGirdisiIso(termin) !== null;
  return (
    <Stack>
      <Text size="sm" c="dimmed">
        DÖF, {olay.workplaceAd} işyerine ve bu olay kaydına bağlı açılır. Olay, bağlı DÖF'lerin tümü
        kapanınca kendiliğinden kapanır; yeni DÖF kapalı olayı yeniden açar.
      </Text>
      {sebep && (
        <Text size="sm" data-testid="olay-dof-sebep-baglami">
          Sebep: <b>{sebep.sebepAd ?? sebep.metin ?? '—'}</b>
        </Text>
      )}
      <AksiyonFormAlanlari
        atanan={atanan}
        setAtanan={setAtanan}
        kaynak={kaynak}
        setKaynak={setKaynak}
        kaynakTuretilir
        baslik={baslik}
        setBaslik={setBaslik}
        termin={termin}
        setTermin={setTermin}
        aciklama={aciklama}
        setAciklama={setAciklama}
        referansNotu={referansNotu}
        setReferansNotu={setReferansNotu}
      />
      {ac.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="olay-dof-hata">
          {ac.error.message}
        </Text>
      )}
      <Button
        data-testid="olay-dof-onayla"
        disabled={!hazir}
        loading={ac.isPending}
        onClick={() => {
          // ⚠️ `kaynak` KOŞULU KALDIRILDI: alan artık sorulmuyor, dolayısıyla
          // hep null — koşulda bırakmak düğmeyi SESSİZCE ölü bırakırdı
          // (bastığında hiçbir şey olmayan düğme; §2.40).
          if (!hazir || !atanan) return;
          ac.mutate(
            {
              workplaceId: olay.workplaceId,
              atananAppUserId: atanan,
              // Kaynak SUNUCUDA türetilir (T6) — gönderilmez.
              kaynakTipiId: null,
              baslik: baslik.trim(),
              terminTarihi: termin,
              incidentId: olay.id,
              ...(sebep ? { incidentCauseId: sebep.id } : {}),
              aciklama: aciklama.trim() === '' ? null : aciklama.trim(),
              kaynakReferansNotu: `Olay ${olay.takipNo}`,
            },
            {
              onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: ['incidents'] });
                onClose();
              },
            },
          );
        }}
      >
        DÖF aç
      </Button>
    </Stack>
  );
}

/** C9a — araştırma yöntemi (B2·1 kapısı aynen: `sebepYazabilir` değilse
 *  salt okunur — görünür ama işlemeyen kontrol yok, §2.40). */
function YontemBolumu({ olay }: { olay: IncidentDetail }): React.JSX.Element {
  return (
    <Card withBorder data-testid="olay-yontem-bolumu">
      <Title order={5}>Araştırma yöntemi</Title>
      <Box mt="sm">
        <YontemSecici olay={olay} yazabilir={olay.yetkiler.sebepYazabilir} />
      </Box>
    </Card>
  );
}

/**
 * C10 — SEBEP AĞACI DOLDURMA (F4·B2'nin iki-kapı dersi aynen): yüzey yalnız
 * `yetkiler.sebepYazabilir` ile açılır (`sebepYazilabilirMi` ikizi);
 * değilse SALT OKUNUR kilit mesajı — araştırmanın neyi soracağı incelemeye
 * almadan önce de görülebilmeli.
 */
function SebepDoldurmaBolumu({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const yazabilir = olay.yetkiler.sebepYazabilir;
  return (
    <Card withBorder data-testid="olay-sebep-doldurma">
      <Title order={5}>
        Sebep ağacı (doğrudan → sistemsel; kök sebep işareti konsolidasyonda sayılır)
      </Title>
      <Stack mt="sm" gap="xs">
        {yazabilir ? (
          <SebepYuzeyi olay={olay} options={options} />
        ) : (
          <Alert color={ANLAM.bilgi} data-testid="olay-sebep-kilit">
            Sebep ağacı henüz doldurulamaz — önce “Araştırma başlat”. Kurulmuş düğümler aşağıdaki
            “Sebep ağacı” kartında görünür.
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

// ✏️ **`Bes5Bolumu` SÖKÜLDÜ (05.09.2026 — tur 11 / R3-a):** olayda 5x5
// YAZMA yolu yoktur; uç da (`POST incidents/:id/bes5`) kalktı. Karşılığı
// uygunsuzluk detayındaki değerlendirme bölümüdür (§24.1/13).

/**
 * 5x5 TARİHÇESİ (F4·B4; ✏️ tur 11 / R3-a'da GENİŞLEDİ — artık HER akışta).
 *
 * Değerlendirme akışı emekliye ayrıldı ve yazma yolu **hiç kalmadı**; girilmiş
 * veri **silinmez**, salt okunur kalır. §2.40'ın sıralaması: çalışamayacak
 * kontrol **görünmez ya da salt okunur** olur — hata veren en kötüsüdür.
 */
function Bes5Tarihce({ olay }: { olay: IncidentDetail }): React.JSX.Element {
  return (
    <Card withBorder data-testid="olay-bes5-tarihce">
      <Title order={5}>5x5 değerlendirme (tarihçe — salt okunur)</Title>
      <Text size="sm" c="dimmed" mt="xs">
        Bu değerlendirme geçmişte girildi ve kayıtta korunur. Yeni değerlendirme uygunsuzluk
        kaydında yapılır.
      </Text>
      <Group gap="lg" mt="xs">
        <Text size="sm">Olasılık: {olay.olasilik5 ?? '—'}</Text>
        <Text size="sm">Şiddet: {olay.siddet5 ?? '—'}</Text>
        <Text size="sm" fw={600} data-testid="olay-bes5-tarihce-puan">
          Puan: {olay.puan5x5 ?? '—'}
        </Text>
      </Group>
    </Card>
  );
}

/**
 * C5 — etkilenen kişiler + YARALANMA SATIRLARI (A2/B7). Yaralanma soluk
 * satır deseniyle (P1-a) kişinin altına girilir: tür + bölge sözlükten,
 * "Diğer" serbest metin ister (işaret SUNUCUDAN — digerMi).
 */
function EtkilenenlerKarti({
  olay,
  options,
  inceleme,
  kayipGunDuzenlenir,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
  inceleme: boolean;
  /** KG-3 — `yetkiler.kayipGunKapisi`: kapanmış olayda içerik kilidinin tek
   *  istisnası; açık olayda araştırma kapısıyla aynı değer. */
  kayipGunDuzenlenir: boolean;
}): React.JSX.Element {
  const sil = useOlaySatirSil();
  return (
    <Card withBorder data-testid="olay-etkilenenler">
      {/* ✏️ T14: "Ekle" DÜĞMESİ + MODAL ÖLDÜ — giriş listenin sonundaki
          soluk satırdadır (P1-a: soluk satır hem "Ekle" düğmesini hem
          "henüz kayıt yok" kutusunu gereksiz kılar). */}
      <Title order={5}>Etkilenen kişiler</Title>
      <Stack gap="sm" mt="xs">
        {olay.etkilenenler.map((satir) => (
          <Box key={satir.id} data-testid="olay-etkilenen-satiri">
            <Group gap="lg" wrap="wrap">
              <Text size="sm" fw={600}>
                {satir.adSoyad ?? '—'}
              </Text>
              {!inceleme && <Text size="sm">{OLAY_TARAF_ETIKET[satir.taraf]}</Text>}
              {inceleme ? (
                <EtkilenenSatirDuzenleme olay={olay} satir={satir} />
              ) : kayipGunDuzenlenir ? (
                // KG-3 — KAPANMIŞ OLAY: basamak metin, yalnız kayıp gün alanı açık
                // (sunucu öteki alanları 409 ile reddeder; ekran göstermez).
                <EtkilenenSatirDuzenleme olay={olay} satir={satir} yalnizKayipGun />
              ) : (
                <>
                  <Text size="sm">{SIDDET_SIRA_ETIKET[satir.siddetBasamagi]}</Text>
                  <Text size="sm">{satir.kayipGun !== null ? `${satir.kayipGun} gün` : '—'}</Text>
                </>
              )}
              {/* Madde 309-b: satır düzeyinde işaret — aynı yüklem (ölümlü sayılmaz). */}
              {kayipGunEksikMi(satir.taraf, satir.siddetBasamagi, satir.kayipGun) && (
                <Text size="xs" c={ANLAM.dikkat} data-testid="olay-etkilenen-kayip-eksik">
                  kayıp gün girilmemiş
                </Text>
              )}
              {inceleme && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color={ANLAM.engel}
                  onClick={() =>
                    sil.mutate({ id: olay.id, liste: 'affected-persons', satirId: satir.id })
                  }
                >
                  Kaldır
                </Button>
              )}
            </Group>
            <YaralanmaSatirlari olay={olay} options={options} satir={satir} inceleme={inceleme} />
          </Box>
        ))}
        {olay.etkilenenler.length === 0 && !inceleme && (
          <Text size="sm" c="dimmed">
            Etkilenen kişi kaydı yok.
          </Text>
        )}
        {inceleme && <EtkilenenSolukSatiri olay={olay} options={options} />}
        {/* KG-3 — kapanmış olayda kayıp gün düzenlemesini yazamayana TEK sebep
            satırı (P13: kapı adım listesinden — şapka/bahçe · reddedilmiş · arşiv).
            Açık olayda araştırma bölümünün kendi sebep satırı var; tekrar yazılmaz. */}
        {!inceleme && olay.kapali && (
          <KapiSebebi
            durum={olay.yetkiler.kayipGunKapisi}
            nesne="Kapanmış olayda kayıp gününü"
            fiil="düzenler"
            testId="olay-kayip-gun-yazamama-sebebi"
          />
        )}
      </Stack>
    </Card>
  );
}

/** B7 — yaralanma satırları: tür + bölge ızgarada, soluk satırla girilir;
 *  kaydedince altında yenisi (P1-a). Rapora ÇIKAR; tedavi notu çıkmaz. */
function YaralanmaSatirlari({
  olay,
  options,
  satir,
  inceleme,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
  satir: IncidentDetail['etkilenenler'][number];
  inceleme: boolean;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const sil = useOlaySatirSil();
  const [turId, setTurId] = useState<string | null>(null);
  const [bolgeId, setBolgeId] = useState<string | null>(null);
  const [serbestMetin, setSerbestMetin] = useState('');
  const digerSecili =
    (options.yaralanmaTurleri.find((t) => t.id === turId)?.digerMi ?? false) ||
    (options.vucutBolgeleri.find((b) => b.id === bolgeId)?.digerMi ?? false);
  const dokunuldu = turId !== null || bolgeId !== null;
  const kaydedilebilir =
    turId !== null && bolgeId !== null && (!digerSecili || serbestMetin.trim() !== '');
  return (
    <Table ml="lg" w="auto" mt={4}>
      <Table.Tbody>
        {satir.yaralanmalar.map((y) => (
          <Table.Tr key={y.id} data-testid="olay-yaralanma-satiri">
            <Table.Td>
              <Text size="sm">
                {y.yaralanmaTuruAd} — {y.vucutBolgesiAd}
                {y.serbestMetin ? ` (${y.serbestMetin})` : ''}
              </Text>
            </Table.Td>
            <Table.Td w={60}>
              {inceleme && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color={ANLAM.engel}
                  data-testid="olay-yaralanma-sil"
                  onClick={() => sil.mutate({ id: olay.id, liste: 'injuries', satirId: y.id })}
                >
                  Kaldır
                </Button>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
        {inceleme && (
          <SolukSatir
            testId="olay-yaralanma-soluk-satir"
            dokunuldu={dokunuldu}
            kaydedilebilir={kaydedilebilir && !eylem.isPending}
            kaydediliyor={eylem.isPending}
            kolonSayisi={1}
            onKaydet={() => {
              if (!kaydedilebilir || !turId || !bolgeId) return;
              eylem.mutate(
                {
                  id: olay.id,
                  eylem: 'injuries',
                  govde: {
                    affectedPersonId: satir.id,
                    yaralanmaTuruRefId: turId,
                    vucutBolgesiRefId: bolgeId,
                    ...(serbestMetin.trim() !== '' ? { serbestMetin: serbestMetin.trim() } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    setTurId(null);
                    setBolgeId(null);
                    setSerbestMetin('');
                  },
                },
              );
            }}
          >
            <Table.Td>
              <Group gap="xs" wrap="wrap">
                <Select
                  size="xs"
                  searchable
                  placeholder="Yaralanma türü"
                  w={200}
                  data={options.yaralanmaTurleri.map((t) => ({ value: t.id, label: t.ad }))}
                  value={turId}
                  onChange={setTurId}
                  data-testid="olay-yaralanma-tur"
                />
                <Select
                  size="xs"
                  searchable
                  placeholder="Vücut bölgesi"
                  w={180}
                  data={options.vucutBolgeleri.map((b) => ({ value: b.id, label: b.ad }))}
                  value={bolgeId}
                  onChange={setBolgeId}
                  data-testid="olay-yaralanma-bolge"
                />
                {digerSecili && (
                  <TextInput
                    size="xs"
                    placeholder='Serbest metin ("Diğer" kaleminde zorunlu)'
                    w={240}
                    value={serbestMetin}
                    onChange={(event) => setSerbestMetin(event.currentTarget.value)}
                    data-testid="olay-yaralanma-diger"
                  />
                )}
              </Group>
            </Table.Td>
          </SolukSatir>
        )}
      </Table.Tbody>
    </Table>
  );
}

/** C6 — tanıklar ve ifadeleri. */
/**
 * TANIKLAR (✏️ T14 — MODAL ÖLDÜ, SATIR İÇİ EKLEME GELDİ).
 *
 * ⚠️ P1: tanık bir **ALT KALEMDİR** (kendi sayfası olamaz) → satır içi
 * girilir. Modal, art arda üç tanık girecek araştırmacıyı her kalemde
 * ekran değiştirmeye zorluyordu — sözleşmenin "giriş ritmi" gerekçesi.
 * Desen kronolojiden birebir alındı (`SolukSatir`, P1-a).
 */
function TaniklarKarti({
  olay,
  inceleme,
}: {
  olay: IncidentDetail;
  inceleme: boolean;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const options = useOlayOptions();
  const [personId, setPersonId] = useState<string | null>(null);
  const [ad, setAd] = useState('');
  const [ifade, setIfade] = useState('');

  const dokunuldu = personId !== null || ad.trim() !== '' || ifade.trim() !== '';
  const kaydedilebilir = ifade.trim() !== '' && (personId !== null || ad.trim() !== '');

  return (
    <Card withBorder data-testid="olay-taniklar">
      <Title order={5}>Tanıklar</Title>
      <Table mt="xs">
        <Table.Tbody>
          {olay.taniklar.map((satir) => (
            <Table.Tr key={satir.id} data-testid="olay-tanik-satiri">
              <Table.Td w={220}>{satir.adSoyad ?? '—'}</Table.Td>
              <Table.Td>{satir.ifade}</Table.Td>
              {/* ⚠️ TANIK SİLME UCU YOK ve bu tur AÇILMADI: T14 yalnız
                  EKLEME desenini modaldan satıra taşıdı. Silme ucu olmadan
                  düğme koymak, basılınca hiçbir şey yapmayan bir kontrol
                  üretirdi (§2.40) — eksik, düğmeyle örtülmez. */}
            </Table.Tr>
          ))}
          {inceleme && (
            <SolukSatir
              testId="olay-tanik-soluk-satir"
              dokunuldu={dokunuldu}
              kaydedilebilir={kaydedilebilir && !eylem.isPending}
              kaydediliyor={eylem.isPending}
              kolonSayisi={2}
              onKaydet={() => {
                if (!kaydedilebilir) return;
                eylem.mutate(
                  {
                    id: olay.id,
                    eylem: 'witnesses',
                    govde: {
                      ifade: ifade.trim(),
                      ...(personId ? { personId } : { ad: ad.trim() }),
                    },
                  },
                  {
                    onSuccess: () => {
                      setPersonId(null);
                      setAd('');
                      setIfade('');
                    },
                  },
                );
              }}
            >
              <Table.Td w={220}>
                <Stack gap={4}>
                  <Select
                    size="xs"
                    searchable
                    clearable
                    placeholder="Kayıtlı kişi"
                    data={(options.data?.personeller ?? []).map((p) => ({
                      value: p.id,
                      label: p.adSoyad,
                    }))}
                    value={personId}
                    onChange={setPersonId}
                    data-testid="olay-tanik-kisi"
                  />
                  {personId === null && (
                    <TextInput
                      size="xs"
                      placeholder="Ad (kayıtlı değilse)"
                      value={ad}
                      onChange={(event) => setAd(event.currentTarget.value)}
                      data-testid="olay-tanik-ad"
                    />
                  )}
                </Stack>
              </Table.Td>
              <Table.Td>
                <Textarea
                  size="xs"
                  autosize
                  minRows={1}
                  placeholder="İfade"
                  value={ifade}
                  onChange={(event) => setIfade(event.currentTarget.value)}
                  data-testid="olay-tanik-ifade"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {!inceleme && olay.taniklar.length === 0 && (
        <Text size="sm" c="dimmed">
          Tanık kaydı yok.
        </Text>
      )}
    </Card>
  );
}

/** C12 — hasar kalemleri; toplam TÜRETİLİR. */
function HasarlarKarti({
  olay,
  inceleme,
}: {
  olay: IncidentDetail;
  inceleme: boolean;
}): React.JSX.Element {
  const sil = useOlaySatirSil();
  return (
    <Card withBorder data-testid="olay-hasarlar">
      <Group justify="space-between">
        <Title order={5}>Hasar kalemleri (toplam türetilir: {olay.hasarTutari ?? 0})</Title>
        {inceleme && <HasarEkle olay={olay} />}
      </Group>
      <Table mt="xs">
        <Table.Tbody>
          {olay.hasarlar.map((satir) => (
            <Table.Tr key={satir.id}>
              <Table.Td>{satir.kalem}</Table.Td>
              <Table.Td>{satir.tutar ?? '—'}</Table.Td>
              <Table.Td>
                {inceleme && (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color={ANLAM.engel}
                    onClick={() => sil.mutate({ id: olay.id, liste: 'damages', satirId: satir.id })}
                  >
                    Kaldır
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

/**
 * ETKİLENEN KİŞİ — SATIR İÇİ EKLEME (✏️ T14; eski modal ÖLDÜ).
 *
 * ⚠️ P1: etkilenen kişi bir **ALT KALEMDİR** → satır içi girilir. Alanlar
 * çok olduğu için **SATIR GENİŞLER, ayrı sayfaya/modala KAÇILMAZ** (P1-b —
 * kaçmak P1'i fiilen iptal ederdi): kimlik + taraf + şiddet + kayıp gün
 * ilk satırda, tedavi notu ikinci satırda.
 *
 * ⚠️ **TEDAVİ NOTU ŞERHİ KORUNDU** — alan araştırmacıya görünür ama RAPORA
 * ÇIKMAZ (sağlık sınırının çıktı kolu, §2.3); etiketi bunu ve *"tanı/tetkik
 * yazmayın"* uyarısını söylemeye devam eder.
 */
/**
 * Madde 310 — DEĞİŞİKLİK GEÇMİŞİ linki + yan panel. Değer TEXT'tir (taraf enum
 * token'ı, sayılar dizge); etiket BURADA çözülür — alan başına tek okuyucu.
 */
function izDegeriEtiketi(
  alan: IncidentDetail['degisiklikGecmisi'][number]['alan'],
  deger: string | null,
): string {
  if (deger === null) return '—';
  if (alan === 'kayip_gun') return `${deger} gün`;
  if (alan === 'siddet_basamagi') {
    const etiket = SIDDET_SIRA_ETIKET[Number(deger) as keyof typeof SIDDET_SIRA_ETIKET];
    return etiket ? `${deger} — ${etiket}` : deger;
  }
  return OLAY_TARAF_ETIKET[deger as keyof typeof OLAY_TARAF_ETIKET] ?? deger;
}
const IZ_ALAN_ETIKET: Record<IncidentDetail['degisiklikGecmisi'][number]['alan'], string> = {
  siddet_basamagi: 'Şiddet basamağı',
  kayip_gun: 'Kayıp gün',
  taraf: 'Taraf',
};
function DegisiklikGecmisi({ olay }: { olay: IncidentDetail }): React.JSX.Element {
  const [acik, setAcik] = useState(false);
  return (
    <>
      <Button
        size="compact-xs"
        variant="subtle"
        onClick={() => setAcik(true)}
        data-testid="olay-degisiklik-gecmisi-ac"
      >
        Değişiklik geçmişi ({olay.degisiklikGecmisi.length})
      </Button>
      <Drawer
        opened={acik}
        onClose={() => setAcik(false)}
        position="right"
        title="Değişiklik geçmişi"
        closeButtonProps={{ 'aria-label': 'Kapat' }}
        data-testid="olay-degisiklik-gecmisi-panel"
      >
        <Stack gap="xs">
          {olay.degisiklikGecmisi.map((d) => (
            <Text size="sm" key={d.id} data-testid="olay-degisiklik-satiri">
              <Text span fw={600}>
                {IZ_ALAN_ETIKET[d.alan]}
              </Text>
              {d.etkilenenAdSoyad ? ` (${d.etkilenenAdSoyad})` : ''} ·{' '}
              {izDegeriEtiketi(d.alan, d.eskiDeger)} → {izDegeriEtiketi(d.alan, d.yeniDeger)} ·{' '}
              {d.yapanAd ?? '—'}
              {d.sapka
                ? ` (${SAPKA_ETIKET[d.sapka as keyof typeof SAPKA_ETIKET] ?? d.sapka})`
                : ''}{' '}
              · {zamanGoster(d.createdAt)}
            </Text>
          ))}
        </Stack>
      </Drawer>
    </>
  );
}

/**
 * Etkilenen satırının BASAMAK + KAYIP GÜN düzenlemesi (madde 309-e, 08.10.2026)
 * — yalnız inceleme kipinde. Basamak seçimi hemen yazılır; kayıp gün odak
 * çıkınca / Enter'da yazılır (değer değiştiyse).
 *
 * ⚠️ ÖLÜMLÜYE GEÇİŞTE `kayipGun` GÖNDERİLMEZ: sunucu mevcut değeri AYNI
 * işlemde NULL yapar (tek yüklem serviste). Ekran "alan temizlendi" bilgisini
 * YANITTAN okur — gönderimden önceki değer doluydu, dönen satır boş ve
 * ölümlü. Ters geçişte alan boş kalır; kalıcı uyarı (309-b) satırda zaten
 * çizilir, bilgi satırı söner. Bu bileşen kural yazmaz: pasiflik ve sebep
 * `kayipGunGirilebilirMi`den (309-a ile aynı yüklem).
 */
function EtkilenenSatirDuzenleme({
  olay,
  satir,
  yalnizKayipGun = false,
}: {
  olay: IncidentDetail;
  satir: IncidentDetail['etkilenenler'][number];
  /** KG-3 — kapanmış olay: basamak seçimi ÇİZİLMEZ (metin), yalnız kayıp gün. */
  yalnizKayipGun?: boolean;
}): React.JSX.Element {
  const guncelle = useOlayEtkilenenGuncelle();
  const [kayipGun, setKayipGun] = useState<number | string>(satir.kayipGun ?? '');
  // Bilgi satırı yalnız ölümlüye geçişte sunucunun temizlediği değer için;
  // sonraki her yazma (basamak düşüşü dahil) onu söndürür.
  const [temizlendi, setTemizlendi] = useState(false);
  useEffect(() => {
    setKayipGun(satir.kayipGun ?? '');
  }, [satir.kayipGun]);
  const olumlu = !kayipGunGirilebilirMi(satir.siddetBasamagi);

  const kayipGunuYaz = (): void => {
    const yeni = kayipGun === '' ? null : Number(kayipGun);
    if (yeni === satir.kayipGun || guncelle.isPending || olumlu) return;
    setTemizlendi(false);
    guncelle.mutate({ id: olay.id, satirId: satir.id, govde: { kayipGun: yeni } });
  };

  return (
    <>
      {/* Madde 310 — TARAF yalnız aynı kimlik sınıfı içinde (seçenekler sunucu
          kuralının ikizi — `tarafKimlikSinifi`; sınıf dışı 422). Kapanmış
          olayda çizilmez (yalnız kayıp gün). */}
      {!yalnizKayipGun && (
        <Select
          size="xs"
          w={150}
          data={Object.entries(OLAY_TARAF_ETIKET)
            .filter(
              ([deger]) =>
                tarafKimlikSinifi(deger as IncidentTarafToken) === tarafKimlikSinifi(satir.taraf),
            )
            .map(([value, label]) => ({ value, label }))}
          value={satir.taraf}
          disabled={guncelle.isPending}
          onChange={(deger) => {
            if (deger === null || deger === satir.taraf) return;
            setTemizlendi(false);
            guncelle.mutate({
              id: olay.id,
              satirId: satir.id,
              govde: { taraf: deger as IncidentTarafToken },
            });
          }}
          data-testid="olay-etkilenen-satir-taraf"
        />
      )}
      {yalnizKayipGun ? (
        <Text size="sm" data-testid="olay-etkilenen-satir-basamak-metin">
          {SIDDET_SIRA_ETIKET[satir.siddetBasamagi]}
        </Text>
      ) : (
        <Select
          size="xs"
          w={190}
          data={Object.entries(SIDDET_SIRA_ETIKET).map(([sira, etiket]) => ({
            value: sira,
            label: `${sira} — ${etiket}`,
          }))}
          value={String(satir.siddetBasamagi)}
          disabled={guncelle.isPending}
          onChange={(deger) => {
            if (deger === null || Number(deger) === satir.siddetBasamagi) return;
            const hedefOlumlu = !kayipGunGirilebilirMi(Number(deger));
            const doluydu = satir.kayipGun !== null;
            guncelle.mutate(
              { id: olay.id, satirId: satir.id, govde: { siddetBasamagi: Number(deger) } },
              {
                onSuccess: (detay) => {
                  const yeniSatir = detay.etkilenenler.find((e) => e.id === satir.id);
                  setTemizlendi(hedefOlumlu && doluydu && yeniSatir?.kayipGun === null);
                },
              },
            );
          }}
          data-testid="olay-etkilenen-satir-basamak"
        />
      )}
      <NumberInput
        size="xs"
        w={120}
        min={0}
        placeholder="Kayıp gün"
        value={kayipGun}
        onChange={setKayipGun}
        onBlur={kayipGunuYaz}
        onKeyDown={(e) => {
          if (e.key === 'Enter') kayipGunuYaz();
        }}
        disabled={olumlu || guncelle.isPending}
        data-testid="olay-etkilenen-satir-kayip"
      />
      {olumlu && temizlendi && (
        <Text size="xs" c="dimmed" data-testid="olay-etkilenen-satir-kayip-temizlendi">
          Ölümlü basamakta kayıp gün girilmez; alan temizlendi.
        </Text>
      )}
      {olumlu && !temizlendi && (
        <Text size="xs" c="dimmed" data-testid="olay-etkilenen-satir-kayip-sebep">
          Ölümlü vakada kayıp gün girilmez.
        </Text>
      )}
      {guncelle.isError && (
        <Text size="xs" c={ANLAM.engel} data-testid="olay-etkilenen-satir-hata">
          {guncelle.error.message}
        </Text>
      )}
    </>
  );
}

function EtkilenenSolukSatiri({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const [taraf, setTaraf] = useState<string | null>('employee');
  const [personId, setPersonId] = useState<string | null>(null);
  const [ad, setAd] = useState('');
  const [basamak, setBasamak] = useState<string | null>('0');
  const [kayipGun, setKayipGun] = useState<number | string>('');
  const [tedaviNotu, setTedaviNotu] = useState('');

  const kisiKaydiGerekli = taraf === 'employee' || taraf === 'contractor_worker';
  const kimlikVar = kisiKaydiGerekli ? personId !== null : ad.trim() !== '';
  const dokunuldu = kimlikVar || kayipGun !== '' || tedaviNotu.trim() !== '';
  const kaydedilebilir = Boolean(taraf) && Boolean(basamak) && kimlikVar;

  const sifirla = (): void => {
    setPersonId(null);
    setAd('');
    setKayipGun('');
    setTedaviNotu('');
    setBasamak('0');
  };

  return (
    <Table w="100%" data-testid="olay-etkilenen-ekleme">
      <Table.Tbody>
        <SolukSatir
          testId="olay-etkilenen-soluk-satir"
          dokunuldu={dokunuldu}
          kaydedilebilir={kaydedilebilir && !eylem.isPending}
          kaydediliyor={eylem.isPending}
          kolonSayisi={1}
          onKaydet={() => {
            if (!kaydedilebilir || !taraf || !basamak) return;
            eylem.mutate(
              {
                id: olay.id,
                eylem: 'affected-persons',
                govde: {
                  taraf,
                  siddetBasamagi: Number(basamak),
                  ...(kisiKaydiGerekli ? { personId } : { ad: ad.trim() }),
                  ...(kayipGun === '' ? {} : { kayipGun: Number(kayipGun) }),
                  ...(tedaviNotu.trim() === '' ? {} : { tedaviNotu: tedaviNotu.trim() }),
                },
              },
              { onSuccess: sifirla },
            );
          }}
        >
          <Table.Td>
            <Stack gap={4}>
              <Group gap="xs" align="flex-start" wrap="wrap">
                <Select
                  size="xs"
                  w={150}
                  placeholder="Taraf"
                  data={Object.entries(OLAY_TARAF_ETIKET).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                  value={taraf}
                  onChange={(deger) => {
                    setTaraf(deger);
                    // Taraf değişince kimlik alanı da değişir; eski değeri
                    // taşımak "seçtiğim kişi kayboldu" hissi verirdi.
                    setPersonId(null);
                    setAd('');
                  }}
                  data-testid="olay-etkilenen-taraf"
                />
                {kisiKaydiGerekli ? (
                  <Select
                    size="xs"
                    w={200}
                    searchable
                    placeholder="Kişi"
                    data={(options.personeller ?? []).map((p) => ({
                      value: p.id,
                      label: p.adSoyad,
                    }))}
                    value={personId}
                    onChange={setPersonId}
                    data-testid="olay-etkilenen-kisi"
                  />
                ) : (
                  <TextInput
                    size="xs"
                    w={200}
                    placeholder="Ad (KVKK asgari — kişi kaydı açılmaz)"
                    value={ad}
                    onChange={(event) => setAd(event.currentTarget.value)}
                    data-testid="olay-etkilenen-ad"
                  />
                )}
                <Select
                  size="xs"
                  w={200}
                  placeholder="Şiddet basamağı"
                  data={Object.entries(SIDDET_SIRA_ETIKET).map(([sira, etiket]) => ({
                    value: sira,
                    label: `${sira} — ${etiket}`,
                  }))}
                  value={basamak}
                  onChange={(deger) => {
                    setBasamak(deger);
                    // Madde 309-a: ölümlü basamağa geçince kayıp gün boşalır —
                    // alan pasifleşir; dolu değer sessizce taşınmaz.
                    if (deger !== null && !kayipGunGirilebilirMi(Number(deger))) setKayipGun('');
                  }}
                  data-testid="olay-etkilenen-basamak"
                />
                {/* Madde 309-a (08.10.2026): ÖLÜMLÜ BASAMAKTA KAYIP GÜN GİRİLMEZ —
                    alan pasif + sebep yanında (gorsel-dil §9: çalışamayan kontrol
                    pasif, sebebi yanında). Kural servisle AYNI yüklem; sunucu
                    dolu değeri 422 ile reddeder. */}
                <NumberInput
                  size="xs"
                  w={120}
                  min={0}
                  placeholder="Kayıp gün"
                  value={kayipGun}
                  onChange={setKayipGun}
                  disabled={basamak !== null && !kayipGunGirilebilirMi(Number(basamak))}
                  data-testid="olay-etkilenen-kayip"
                />
                {basamak !== null && !kayipGunGirilebilirMi(Number(basamak)) && (
                  <Text size="xs" c="dimmed" data-testid="olay-etkilenen-kayip-sebep">
                    Ölümlü vakada kayıp gün girilmez.
                  </Text>
                )}
              </Group>
              {/* Tedavi notu: araştırmacıya görünür, RAPORA ÇIKMAZ (sağlık
                  sınırının çıktı kolu — incident-rapor). Serbest metindir;
                  tanı/tetkik değeri yazılmaz (§2.3). */}
              <TextInput
                size="xs"
                placeholder="Tedavi notu (rapora çıkmaz — tanı/tetkik yazmayın)"
                value={tedaviNotu}
                onChange={(event) => setTedaviNotu(event.currentTarget.value)}
                data-testid="olay-etkilenen-tedavi"
              />
              {eylem.isError && (
                <Text size="xs" c={ANLAM.engel} role="alert">
                  {eylem.error.message}
                </Text>
              )}
            </Stack>
          </Table.Td>
        </SolukSatir>
      </Table.Tbody>
    </Table>
  );
}

function HasarEkle({ olay }: { olay: IncidentDetail }): React.JSX.Element {
  const eylem = useOlayEylem();
  const [acik, setAcik] = useState(false);
  const [kalem, setKalem] = useState('');
  const [tutar, setTutar] = useState<number | string>('');
  return (
    <>
      <Button
        size="compact-sm"
        variant="light"
        data-testid="olay-hasar-ekle"
        onClick={() => setAcik(true)}
      >
        Ekle
      </Button>
      <Modal opened={acik} onClose={() => setAcik(false)} title="Hasar kalemi ekle">
        <Stack>
          <TextInput
            label="Kalem"
            required
            data-testid="olay-hasar-kalem"
            value={kalem}
            onChange={(event) => setKalem(event.currentTarget.value)}
          />
          <NumberInput
            label="Tutar"
            min={0}
            data-testid="olay-hasar-tutar"
            value={tutar}
            onChange={setTutar}
          />
          <Button
            data-testid="olay-hasar-onayla"
            disabled={kalem.trim() === ''}
            loading={eylem.isPending}
            onClick={() =>
              eylem.mutate(
                {
                  id: olay.id,
                  eylem: 'damages',
                  govde: {
                    kalem: kalem.trim(),
                    ...(tutar === '' ? {} : { tutar: Number(tutar) }),
                  },
                },
                { onSuccess: () => setAcik(false) },
              )
            }
          >
            Ekle
          </Button>
        </Stack>
      </Modal>
    </>
  );
}

/** Değişmez geçiş log'u (incident_transition — ptw izi deseni). */
function GecisGecmisi({ olay }: { olay: IncidentDetail }): React.JSX.Element {
  if (olay.gecisler.length === 0) return <></>;
  return (
    <Card withBorder data-testid="olay-gecisler">
      <Title order={5}>Geçiş geçmişi</Title>
      <Table mt="xs">
        <Table.Tbody>
          {olay.gecisler.map((gecis, index) => (
            <Table.Tr key={index}>
              <Table.Td>{zamanGoster(gecis.createdAt)}</Table.Td>
              <Table.Td>{olayIslemEtiketi(gecis.islem, olay.akis)}</Table.Td>
              <Table.Td>{gecis.yapanAd ?? 'Sistem/Anonim'}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
